// HTTP-Fallback für Trail-Höhenprofile — Multi-Provider-Cascade.
//
// Hintergrund (Wave 14at/au):
//   MapLibre's `queryTerrainElevation` ist primär offline-first, aber hängt
//   an mehreren Bedingungen (DEM-Source im Style, setTerrain aktiv, Tiles
//   geladen). Wenn eine davon fehlt → `null` → Profil bleibt flach.
//
//   Beobachtet: selbst nach fitBounds-vor-Rebuild (Wave 14as) bleibt bei
//   einigen Clients alles flach → Terrain-Setup auf diesem Gerät ist grund-
//   sätzlich aus. Der Rebuild-Button produziert "No data" ohne Fortschritt.
//
// Strategie: Cascade durch 3 kostenlose HTTP-APIs. Der erste der liefert
// gewinnt — jeder kann einzeln ausfallen (CORS, Rate-Limit, Down, 403,
// Cloudflare-Blocker) ohne dass der ganze Fallback kaputt ist.
//
//   1. open-meteo.com/v1/elevation   — Cloudflare-CDN, GET, 10k/day, CORS
//   2. api.opentopodata.org/v1/eudem25m  — EU-25m, POST, 1k/day, 1/sec
//   3. api.open-elevation.com/api/v1/lookup  — SRTM90, POST, flaky aber da
//
// Cache-Strategie:
//   Ergebnis geht durch denselben `saveCachedSrtm`-Pfad wie MapLibre-
//   Samples → IDB-Store `trailElevations` → einmal online fetched, danach
//   offline verfügbar bis Track-Länge sich >50m ändert.
//
// Offline-Verhalten:
//   fetch() wirft "Failed to fetch" / "Load failed" bei offline → alle
//   3 Provider returnen null → Hook fällt auf source='none' zurück.
//   Kein Regression-Risk.

import type { GpxTrack } from '@adtu/shared';
import { cumulativeDistances } from './trailGeom';

const FETCH_TIMEOUT_MS = 8000;
const MIN_VALID_RATIO = 0.3; // ≥30% Samples müssen valide sein
const MIN_SPREAD_METERS = 2; // Min/Max-Diff ≥ 2m (sonst flatline-Verdacht)

interface SamplePoint {
  lat: number;
  lon: number;
}

/**
 * Sampled N Punkte gleichmäßig entlang Distanz — identisch zu buildSrtm­
 * ElevationSamples, damit Indexierung kompatibel zu sampleDistances() ist.
 */
function sampleTrackLatLons(
  track: GpxTrack,
  sampleCount: number,
): SamplePoint[] | null {
  const points = track.points;
  if (points.length < 2) return null;
  const cum = cumulativeDistances(points);
  const total = cum[cum.length - 1] ?? 0;
  if (total === 0) return null;

  const out: SamplePoint[] = [];
  for (let s = 0; s < sampleCount; s++) {
    const targetDist = (s / (sampleCount - 1)) * total;
    let segI = 0;
    while (segI < cum.length - 1 && (cum[segI + 1] ?? 0) < targetDist) segI++;
    const dStart = cum[segI] ?? 0;
    const dEnd = cum[segI + 1] ?? dStart;
    const segLen = dEnd - dStart;
    const t = segLen > 0 ? (targetDist - dStart) / segLen : 0;
    const a = points[segI]!;
    const b = points[Math.min(segI + 1, points.length - 1)]!;
    out.push({
      lat: a.lat + (b.lat - a.lat) * t,
      lon: a.lon + (b.lon - a.lon) * t,
    });
  }
  return out;
}

/**
 * fetch mit Timeout. Gibt Response oder null zurück (bei Timeout/Netzfehler).
 * Kein throw — Cascade soll sauber zum nächsten Provider gehen.
 */
async function timedFetch(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (controller.signal.aborted) {
      console.info(`[elev] http timeout nach ${timeoutMs}ms: ${url}`);
    } else {
      console.info(`[elev] http fetch failed: ${url}`, err);
    }
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

/** Validiert Samples via Ratio + Spread. Returns clean array oder null. */
function validateSamples(
  raw: (number | null | undefined)[],
  providerName: string,
): number[] | null {
  const sampleCount = raw.length;
  const out: number[] = [];
  let validCount = 0;
  let lastValid: number | null = null;
  let minElev = Infinity;
  let maxElev = -Infinity;
  for (const v of raw) {
    if (v != null && Number.isFinite(v)) {
      validCount++;
      lastValid = v;
      out.push(v);
      if (v < minElev) minElev = v;
      if (v > maxElev) maxElev = v;
    } else {
      out.push(lastValid ?? 0);
    }
  }
  if (validCount < Math.floor(sampleCount * MIN_VALID_RATIO)) {
    console.info(
      `[elev] ${providerName} reject: only ${validCount}/${sampleCount} valid (<${MIN_VALID_RATIO * 100}%)`,
    );
    return null;
  }
  if (maxElev - minElev < MIN_SPREAD_METERS) {
    console.info(
      `[elev] ${providerName} reject: flat spread ${(maxElev - minElev).toFixed(2)}m ` +
        `(min=${minElev.toFixed(0)} max=${maxElev.toFixed(0)})`,
    );
    return null;
  }
  console.info(
    `[elev] ${providerName} accept: ${validCount}/${sampleCount} valid, ` +
      `spread ${(maxElev - minElev).toFixed(0)}m (${minElev.toFixed(0)}..${maxElev.toFixed(0)}m)`,
  );
  return out;
}

// ============================================================================
// Provider 1: open-meteo.com — GET, Cloudflare-CDN, sehr stabil.
// ============================================================================
async function fetchFromOpenMeteo(
  samples: SamplePoint[],
): Promise<number[] | null> {
  const lats = samples.map((s) => s.lat.toFixed(5)).join(',');
  const lons = samples.map((s) => s.lon.toFixed(5)).join(',');
  const url = `https://api.open-meteo.com/v1/elevation?latitude=${lats}&longitude=${lons}`;
  const res = await timedFetch(url, { method: 'GET' }, FETCH_TIMEOUT_MS);
  if (!res) return null;
  if (!res.ok) {
    console.info(`[elev] open-meteo HTTP ${res.status} ${res.statusText}`);
    return null;
  }
  try {
    const json = (await res.json()) as { elevation?: number[] };
    if (!json.elevation || json.elevation.length !== samples.length) {
      console.info(
        `[elev] open-meteo bad payload: expected ${samples.length} elevations, ` +
          `got ${json.elevation?.length ?? 'undefined'}`,
      );
      return null;
    }
    return validateSamples(json.elevation, 'open-meteo');
  } catch (err) {
    console.info('[elev] open-meteo json parse failed', err);
    return null;
  }
}

// ============================================================================
// Provider 2: opentopodata.org eudem25m — POST, EU-25m-Auflösung, 1/sec.
// ============================================================================
async function fetchFromOpentopodata(
  samples: SamplePoint[],
): Promise<number[] | null> {
  const locations = samples
    .map((s) => `${s.lat.toFixed(5)},${s.lon.toFixed(5)}`)
    .join('|');
  const res = await timedFetch(
    'https://api.opentopodata.org/v1/eudem25m',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locations }),
    },
    FETCH_TIMEOUT_MS,
  );
  if (!res) return null;
  if (!res.ok) {
    console.info(`[elev] opentopodata HTTP ${res.status} ${res.statusText}`);
    return null;
  }
  try {
    const json = (await res.json()) as {
      status?: string;
      results?: Array<{ elevation: number | null }>;
      error?: string;
    };
    if (json.status !== 'OK' || !json.results) {
      console.info(
        `[elev] opentopodata status='${json.status}' err='${json.error ?? ''}'`,
      );
      return null;
    }
    if (json.results.length !== samples.length) {
      console.info(
        `[elev] opentopodata bad payload: expected ${samples.length}, got ${json.results.length}`,
      );
      return null;
    }
    return validateSamples(
      json.results.map((r) => r.elevation),
      'opentopodata',
    );
  } catch (err) {
    console.info('[elev] opentopodata json parse failed', err);
    return null;
  }
}

// ============================================================================
// Provider 3: open-elevation.com — POST, SRTM90, flaky aber oft da.
// ============================================================================
async function fetchFromOpenElevation(
  samples: SamplePoint[],
): Promise<number[] | null> {
  const locations = samples.map((s) => ({
    latitude: Number(s.lat.toFixed(5)),
    longitude: Number(s.lon.toFixed(5)),
  }));
  const res = await timedFetch(
    'https://api.open-elevation.com/api/v1/lookup',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locations }),
    },
    FETCH_TIMEOUT_MS,
  );
  if (!res) return null;
  if (!res.ok) {
    console.info(`[elev] open-elevation HTTP ${res.status} ${res.statusText}`);
    return null;
  }
  try {
    const json = (await res.json()) as {
      results?: Array<{ elevation: number | null }>;
    };
    if (!json.results) {
      console.info('[elev] open-elevation no results array');
      return null;
    }
    if (json.results.length !== samples.length) {
      console.info(
        `[elev] open-elevation bad payload: expected ${samples.length}, got ${json.results.length}`,
      );
      return null;
    }
    return validateSamples(
      json.results.map((r) => r.elevation),
      'open-elevation',
    );
  } catch (err) {
    console.info('[elev] open-elevation json parse failed', err);
    return null;
  }
}

/**
 * Wave 14cc-fix5: HARTES Limit der HTTP-Provider.
 *
 * Open-Meteo, Opentopodata UND Open-Elevation akzeptieren alle nur ≤100
 * Locations pro Request — Open-Meteo wegen URL-Länge (GET mit komma-
 * separierten Listen), die anderen wegen API-Policy. Bei mehr Samples
 * antworten alle drei mit Error oder Truncation → unsere Pipeline
 * verwirft das Ergebnis → falscher Fallback auf das (in Liguria
 * teilweise korrupte) Offline-PMTiles.
 *
 * Das war die Regression seit Wave 14cf-fix3: vorher war sampleCount
 * konstant 100, jetzt distanzbasiert (=652 bei 16 km Route). Vor Elba
 * lief HTTP, danach scheiterte es lautlos.
 */
const HTTP_PROVIDER_MAX_SAMPLES = 100;

/**
 * Hauptexport: probiert alle 3 Provider in Reihe, der erste liefernde gewinnt.
 *
 * Wave 14cc-fix5: wenn sampleCount > 100, samplen wir die Track-Strecke
 * auf nur 100 Punkte für die API-Calls und interpolieren das Ergebnis
 * danach linear hoch auf die volle Sample-Anzahl. Räumliche Auflösung
 * bleibt für die Profil-Anzeige praktisch identisch (100 Samples auf
 * 16 km = 160 m Abstand; echte Terrain-Features sind breiter), aber
 * der HTTP-Roundtrip bleibt im Provider-Limit.
 *
 * Reihenfolge bewusst: Cloudflare-CDN zuerst (schnell + stabil), dann
 * EU-hochauflösend, dann der Last-Resort-Provider.
 *
 * Returns null wenn ALLE drei versagen → Hook fällt auf source='none'.
 */
export async function fetchHttpElevations(
  track: GpxTrack,
  sampleCount = 100,
): Promise<number[] | null> {
  // Schritt 1: API-call sample count cappen.
  const apiSampleCount = Math.min(sampleCount, HTTP_PROVIDER_MAX_SAMPLES);
  const apiSamples = sampleTrackLatLons(track, apiSampleCount);
  if (!apiSamples) return null;

  console.info(
    `[elev] http cascade start: requesting ${apiSamples.length} samples ` +
      `(target output ${sampleCount}), first point ` +
      `${apiSamples[0]?.lat.toFixed(4)},${apiSamples[0]?.lon.toFixed(4)}`,
  );

  let apiResult: number[] | null = null;
  apiResult = await fetchFromOpenMeteo(apiSamples);
  if (!apiResult) apiResult = await fetchFromOpentopodata(apiSamples);
  if (!apiResult) apiResult = await fetchFromOpenElevation(apiSamples);

  if (!apiResult) {
    console.info('[elev] http cascade: alle 3 Provider failed');
    return null;
  }

  // Schritt 2: Wenn API-Resultat schon die Ziel-Auflösung hat, direkt zurück.
  if (apiResult.length === sampleCount) return apiResult;

  // Schritt 3: Linear interpolieren auf sampleCount.
  // apiResult sind gleichmäßig verteilte Werte über die Strecke; wir
  // mappen den i-ten Output-Slot auf eine fraktionale Position im
  // apiResult-Index-Raum.
  console.info(
    `[elev] http cascade: upsampling ${apiResult.length} → ${sampleCount} via linear interpolation`,
  );
  return upsampleLinear(apiResult, sampleCount);
}

/**
 * Lineare Aufwärts-Interpolation eines Höhen-Arrays. Beide Achsen
 * gleichmäßig — Input-Index `0..M-1` mappt auf Output-Index `0..N-1`
 * mit demselben relativen Track-Anteil.
 */
function upsampleLinear(input: number[], targetLength: number): number[] {
  const m = input.length;
  if (m === targetLength) return input.slice();
  if (m < 2 || targetLength < 2) return input.slice();
  const out = new Array<number>(targetLength);
  for (let i = 0; i < targetLength; i++) {
    const inputPos = (i / (targetLength - 1)) * (m - 1);
    const i0 = Math.floor(inputPos);
    const i1 = Math.min(m - 1, i0 + 1);
    const t = inputPos - i0;
    out[i] = input[i0]! * (1 - t) + input[i1]! * t;
  }
  return out;
}
